// node_modules
import axios from "axios";
import { LoggingConstants } from "Constants";
// Enums
import { EntityTypeEnum, TechnologyVoteTypeEnum } from "Enums";
// Helpers
import { AxiosHelperSingleton, PendoHelperSingleton } from "Helpers";
// Types
import { TAddPartnerToTechnologyParameter, TPartnerResultDTO, TSavedQueryTempDTO, TSearchTechnologyResultDTO, TTechnology, TTechnologyCopyDTO, TTechnologyCountDTO, TTechnologyPartnerResultsDTO, TTechnologyPatentsDTO, TTechnologyScienceArticlesDTO, TTechnologyTrlDTO } from "Types";

export class TechnologyController {
    public async getTechnologyPartnerResultsAsync(technologyId: number): Promise<TTechnologyPartnerResultsDTO> {
        return (await axios.get(`${AxiosHelperSingleton.getServerBaseURL()}api/technologies/${technologyId}/partners`)).data;
    }

    public async searchTechnologyAsync(name: string): Promise<TSearchTechnologyResultDTO[]> {
        return (await axios.get(`${AxiosHelperSingleton.getServerBaseURL()}api/technologies/search?name=${name}`)).data;
    }

    public async voteAsync(technologyId: number, vote: TechnologyVoteTypeEnum): Promise<boolean> {
        PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}TechnologyVoteYesNoSuper`, {
            technologyId: technologyId
        });
        
        const result = await axios.post(`${AxiosHelperSingleton.getServerBaseURL()}api/technologies/${technologyId}/votes`, { vote });
        return result.status === 200;
    }

    public async rateAsync(technologyId: number, rate: number): Promise<boolean> {
        PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}TechnologyRateFiveStars`, {
            technologyId: technologyId
        });
        
        const result = await axios.post(`${AxiosHelperSingleton.getServerBaseURL()}api/technologies/${technologyId}/rates`, { rate });
        return result.status === 200;
    }

    public async addPartnerFromDeliveryToTechnologyAsync(technologyId: number, addPartnerToTechnologyParameter: TAddPartnerToTechnologyParameter): Promise<TPartnerResultDTO> {
        PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}PartnersAdd`, {
            technologyId: technologyId
        });
        
        return (await axios.post(`${AxiosHelperSingleton.getServerBaseURL()}api/technologies/${technologyId}/delivery/partners`, addPartnerToTechnologyParameter)).data;
    }

    public async removePartnerFromTechnologyAsync(technologyId: number, partnerTitle: string): Promise<boolean> {
        PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}PartnersRemove`, {
            technologyId: technologyId
        });
        
        return (await axios.delete(`${AxiosHelperSingleton.getServerBaseURL()}api/technologies/${technologyId}/partners`, { data: { partnerTitle } })).data;
    }

    public async addScienceArticleToTechnologyAsync(technologyId: number, articleId: number, fromDelivery = false): Promise<boolean> {
        PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}ScienceArticleAdd`, {
            technologyId: technologyId,
            articleId: articleId
        });
        
        let url = `${AxiosHelperSingleton.getServerBaseURL()}api/technologies/${technologyId}/`;
        if (fromDelivery) {
            url += `delivery/sciencearticle/${articleId}`;
        } else {
            url += `sciencearticle/${articleId}`;
        }
        const result = await axios.post(url);
        return result.status === 200;
    }

    public async removeScienceArticleFromDeliveryToTechnologyAsync(technologyId: number, articleId: number): Promise<boolean> {
        PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}ScienceArticleRemove`, {
            technologyId: technologyId,
            articleId: articleId
        });
        
        const result = await axios.delete(`${AxiosHelperSingleton.getServerBaseURL()}api/technologies/${technologyId}/delivery/sciencearticle/${articleId}`);
        return result.status === 200;
    }

    public async addPatentToTechnologyAsync(technologyId: number, patentId: string, fromDelivery = false): Promise<boolean> {
        PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}PatentAdd`, {
            technologyId: technologyId,
            patentId: patentId
        });
        
        let url = `${AxiosHelperSingleton.getServerBaseURL()}api/technologies/${technologyId}/`;
        if (fromDelivery) {
            url += `delivery/patent/${patentId}`;
        } else {
            url += `patent/${patentId}`;
        }
        const result = await axios.post(url);
        return result.status === 200;
    }

    public async removePatentFromDeliveryToTechnologyAsync(technologyId: number, patentId: string): Promise<boolean> {
        PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}PatentRemove`, {
            technologyId: technologyId,
            patentId: patentId
        });
        
        const result = await axios.delete(`${AxiosHelperSingleton.getServerBaseURL()}api/technologies/${technologyId}/delivery/patent/${patentId}`);
        return result.status === 200;
    }

    public async getTechnologyScienceArticleDocumentsAsync(technologyId: number): Promise<TTechnologyScienceArticlesDTO> {
        return (await axios.get(`${AxiosHelperSingleton.getServerBaseURL()}api/technologies/${technologyId}/documents/sciencearticles`)).data;
    }

    public async getTechnologyPatentDocumentsAsync(technologyId: number): Promise<TTechnologyPatentsDTO> {
        return (await axios.get(`${AxiosHelperSingleton.getServerBaseURL()}api/technologies/${technologyId}/documents/patents`)).data;
    }

    public async getTechnologyCountsAsync(technologyId: number): Promise<TTechnologyCountDTO> {
        return (await axios.get(`${AxiosHelperSingleton.getServerBaseURL()}api/technologies/${technologyId}/counts`)).data;
    }

    public async getTechnologySavedQueriesAsync(technologyId: number): Promise<TSavedQueryTempDTO[]> {
        return (await axios.get(`${AxiosHelperSingleton.getServerBaseURL()}api/savedquery/technology/${technologyId}`)).data;
    }

    public async updateTechnology(technologyId: number, fromDelivery: boolean, name?: string, description?: string): Promise<boolean> {
        PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}TechnologyUpdate`, {
            technologyId: technologyId
        });
        
        const data: { [key: string]: string | boolean }  = {
            "fromDelivery": fromDelivery
        };
        if(description !== null && description !== undefined) { data["description"] = description; }
        if(name) { data["name"] = name; }

        const result = await axios.post(`${AxiosHelperSingleton.getServerBaseURL()}api/technologies/${technologyId}/update`, data);
        return result.status === 200;
    }

    public async appendToTechnologyDescription(technologyId: number, description: string, documentId: string): Promise<boolean> {
        PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}TechnologyAppendDescription`, {
            technologyId: technologyId
        });
        
        const formData = new FormData();
        formData.append("id", `${technologyId}`);
        formData.append("Description", description);
        formData.append("IsAppend", `${true}`);
        formData.append("doi", documentId);
        const result = await axios.post<TTechnology>(`${AxiosHelperSingleton.getServerBaseURL()}Technologies/Description`,
            formData, { headers: { "Content-Type": "multipart/form-data" } });
        return result.status === 200;
    }

    public async updateTechnologyTrl(technologyId: number, fromDelivery: boolean, technologyTrl: TTechnologyTrlDTO): Promise<boolean> {
        PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}TechnologyUpdateTrl`, {
            technologyId: technologyId
        });
        
        const result = await axios.post(`${AxiosHelperSingleton.getServerBaseURL()}api/technologies/${technologyId}/updatetrl?fromDelivery=${fromDelivery}`, technologyTrl);
        return result.status === 200;
    }

    public async createAdditionalTechnology(caseId: number, conceptNumbering?: string, technologyName?: string): Promise<boolean> {
        PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}TechnologyCreateAdditional`, {
            caseId: caseId
        });
        
        const formData = new FormData();
        formData.append("caseId", caseId.toString());
        if (conceptNumbering) { formData.append("conceptNumbering", conceptNumbering); }
        if (technologyName) { formData.append("technologyName", technologyName); }
        const result = await axios.post<TTechnology>(`${AxiosHelperSingleton.getServerBaseURL()}api/technologies/additionalresult`,
            formData, { headers: { "Content-Type": "multipart/form-data" } });
        return result.status === 200;
    }

    public async createTechnology(caseId: number, name: string, conceptId: number | undefined = undefined): Promise<number | undefined> {
        PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}TechnologyCreate`, {
            caseId: caseId
        });
        
        const formData = new FormData();
        formData.append("Description", "");
        formData.append("Name", name);
        formData.append("MidwaySelected", "false");
        formData.append("FinalSelected", "false");
        formData.append("Known", "false");
        if (conceptId) { formData.append("ConceptID", `${conceptId}`); }
        formData.append("CaseID", `${caseId}`);

        const result = await axios.post(`${AxiosHelperSingleton.getServerBaseURL()}Technologies/Create`,
            formData, { headers: { "Content-Type": "multipart/form-data" } });
        if (result.status === 200) {
            return result.data.newTechnology.technologyID as number;
        } else {
            return undefined;
        }
    }
    
    public async updateTechnologyAsync(technology: TTechnology): Promise<boolean> {
        PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}TechnologyUpdateClassic`, {
            technologyId: technology.id
        });
        
        const formData = new FormData();
        formData.append("TechnologyID", `${technology.id}`);
        formData.append("Description", `${technology.description ? technology.description : ""}`);
        formData.append("Name", `${technology.name}`);
        formData.append("MidwaySelected", `${false}`);
        formData.append("FinalSelected", `${false}`);
        const result = await axios.post<TTechnology>(`${AxiosHelperSingleton.getServerBaseURL()}Technologies/Edit/${technology.id}`,
            formData, { headers: { "Content-Type": "multipart/form-data" } });
        return result.status === 200;
    }

    public async createEmptyTechnologyAsync(caseId: number): Promise<number | undefined> {
        PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}TechnologyCreateEmpty`, {
            caseId: caseId
        });
        
        const formData = new FormData();
        formData.append("CaseID", `${caseId}`);
        formData.append("Known", `${true}`);
        formData.append("QueryAssistance", `${false}`);
        const result = await axios.post(`${AxiosHelperSingleton.getServerBaseURL()}Technologies/CreateEmpty`,
            formData, { headers: { "Content-Type": "multipart/form-data" } });
        if (result.status === 200) {
            return result.data.technologyID;
        } else {
            return undefined;
        }
    }

    public async addSupplierToTechnology(caseId: number, technologyId: number, supplierTitle: string): Promise<boolean> {
        PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}TechnologySupplierCreate`, {
            technologyId: technologyId
        });
        
        const formData = new FormData();
        formData.append("CaseID", `${caseId}`);
        formData.append("TechnologyID", `${technologyId}`);
        formData.append("Title", supplierTitle);
        formData.append("Type", "1");
        formData.append("GroupName", "Suppliers");
        formData.append("Link", "");
        const result = await axios.post<TTechnology>(`${AxiosHelperSingleton.getServerBaseURL()}Technologies/CreateExtra`,
            formData, { headers: { "Content-Type": "multipart/form-data" } });
        return result.status === 200;
    }
    
    public async deleteTechnologyAsync(technologyId: number): Promise<boolean> {
        const formData = new FormData();
        const result = await axios.post(`${AxiosHelperSingleton.getServerBaseURL()}Technologies/Delete/${technologyId}`,
        formData, { headers: { "Content-Type": "multipart/form-data" } });
        return result.status === 200;
    }

    public async copyTechnologyAsync(caseId: number, technologyId: number): Promise<boolean> {
        const dto: TTechnologyCopyDTO = {
            targetId: caseId.toString(),
            targetType: EntityTypeEnum.CaseType
        };
        const result = await axios.post(`${AxiosHelperSingleton.getServerBaseURL()}api/technologies/${technologyId}`, dto);
        return result.status === 200;
    }
}

export const TechnologyControllerSingleton = new TechnologyController();